// File: src/pages/UpgradeAccountPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

// --- 1. IMPORT TOASTIFY ---
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// --- 2. LOGIC CHÍNH ---
export default function UpgradeAccountPage() {
    const [loading, setLoading] = useState(false);
    const checkRef = useRef(false);
    
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Cổng API
    const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
    
    const [userPlan, setUserPlan] = useState('free');
    const [userRole, setUserRole] = useState('candidate'); 
    const [expiryDate, setExpiryDate] = useState(null);

    const isBusiness = location.pathname.includes('/business') || userRole === 'business';
    const currentConfigKey = isBusiness ? 'business' : 'candidate';

    const PLAN_LEVELS = {
        'free': 0, 'business_free': 0,
        'pro': 1, 'business_pro': 1,
        'premium': 2, 'business_premium': 2
    };

    // Hàm load lại profile
    const fetchCurrentPlan = async () => {
        const token = localStorage.getItem('skillmatch_token');
        if (!token) return;
        try {
            const res = await fetch(`${API_URL}/api/auth/me`, { 
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if(res.ok){
                const data = await res.json();
                
                if (data.role) setUserRole(data.role); 

                let currentPlan = data.subscription?.plan || 'free';

                // --- SỬA ĐOẠN NÀY: Mapping toàn bộ các case của Business ---
                if (data.role === 'business') {
                    // Nếu DB trả về 'free' -> đổi thành 'business_free'
                    if (currentPlan === 'free') currentPlan = 'business_free';
                    
                    // 👇 THÊM 2 DÒNG NÀY: Map gói Pro/Premium thường sang Business
                    if (currentPlan === 'pro') currentPlan = 'business_pro';
                    if (currentPlan === 'premium') currentPlan = 'business_premium';
                }

                setUserPlan(currentPlan);
                if (data.subscription?.endDate) setExpiryDate(data.subscription.endDate);
            } 
        } catch (e) { console.error(e); }
    };

    useEffect(() => {
        fetchCurrentPlan();
    }, []);

    // 3. Xử lý sau thanh toán (Thay Alert/Modal bằng Toast)
    const verifyPayment = async (orderCode) => {
        try {
            const token = localStorage.getItem('skillmatch_token');
            const res = await fetch(`${API_URL}/api/payments/check-status/${orderCode}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!res.ok) {
                console.error("Lỗi server khi check status");
                return;
            }

            const data = await res.json();

            if (data.success) {
                setUserPlan(data.plan); 
                
                // ✅ THAY THẾ MODAL BẰNG TOAST SUCCESS
                toast.success(
                    <div>
                        <h4 className="font-bold text-base">Thanh toán thành công!</h4>
                        <p className="text-sm">Chào mừng bạn đến với gói VIP.</p>
                    </div>, 
                    { icon: "🎉" }
                );

                await fetchCurrentPlan();

                const event = new Event('auth-change');
                window.dispatchEvent(event);
            } else {
                // ✅ THAY ALERT BẰNG TOAST ERROR
                toast.error("Thanh toán chưa hoàn tất hoặc đang xử lý.");
            }
        } catch (error) {
            console.error(error);
            toast.error("Lỗi kết nối khi kiểm tra thanh toán.");
        }
    };

    useEffect(() => {
        if (checkRef.current) return;

        const status = searchParams.get('status');
        const code = searchParams.get('code');
        const cancel = searchParams.get('cancel');
        const orderCode = searchParams.get('orderCode');

        // 👇 THÊM LOG ĐỂ DEBUG
        console.log("🔍 URL Params:", { status, code, cancel, orderCode });

        if (!status && !code && !cancel && !orderCode) return;
        
        checkRef.current = true;

        if (status === 'CANCELLED' || cancel === 'true') {
            toast.info("Giao dịch đã bị hủy.");
            navigate(location.pathname, { replace: true });
            return;
        }

        // Logic check success mở rộng hơn
        const isSuccess = status === 'PAID' || status === 'SUCCESS' || code === '00';
        
        if (isSuccess && orderCode) {
            console.log("🚀 Bắt đầu verify payment cho đơn:", orderCode); // Log xác nhận chạy
            verifyPayment(orderCode); 
            window.history.replaceState(null, '', location.pathname); 
        } else {
            console.warn("⚠️ Không thỏa mãn điều kiện verify:", { isSuccess, orderCode });
        }
    }, [searchParams, location.pathname, navigate]);

    const handleUpgrade = async (planKey) => {
        if (planKey === 'free') return;
        setLoading(true);
        try {
            const token = localStorage.getItem('skillmatch_token');
            if (!token) {
                toast.warn("Vui lòng đăng nhập để nâng cấp.");
                return navigate('/login');
            }

            const res = await fetch(`${API_URL}/api/payments/create-payment-link`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ planKey, amount: 50000 }) 
            });

            if (!res.ok) {
                const errText = await res.text();
                throw new Error(`Server Error: ${res.status} - ${errText}`);
            }

            const data = await res.json();
            if (data.checkoutUrl) window.location.href = data.checkoutUrl;
            else toast.error("Không nhận được link thanh toán từ hệ thống.");

        } catch (error) {
            console.error("Lỗi Handle Upgrade:", error);
            toast.error("Có lỗi xảy ra khi tạo thanh toán. Vui lòng thử lại.");
        } finally {
            setLoading(false);
        }
    };

    const getDaysRemaining = () => {
        if (!expiryDate) return 0;
        const now = new Date();
        const end = new Date(expiryDate);
        if (now > end) return 0;
        return Math.ceil(Math.abs(end - now) / (1000 * 60 * 60 * 24)); 
    };

    // --- CONFIG GÓI CƯỚC (Giữ nguyên) ---
    const PLANS = {
        candidate: {
            title: "Bứt phá sự nghiệp với SkillMatch VIP",
            subtitle: "Đầu tư cho tương lai với chi phí chỉ bằng một ly cà phê mỗi tháng.",
            packs: [
                {
                    id: 'free',
                    name: 'Thường (Free)',
                    desc: 'Trải nghiệm cơ bản',
                    price: '0đ',
                    unit: '/vĩnh viễn',
                    features: [
                        { text: "Làm 3 bài test năng lực/ngày", included: true },
                        { text: "Ứng tuyển 3 công việc/ngày", included: true },
                        { text: "Rà soát & Đánh giá CV cơ bản", included: true },
                        { text: "Phỏng vấn thử mô phỏng (15p/ngày)", included: true },
                        { text: "Xem biểu đồ phân tích năng lực", included: false },
                        { text: "Cố vấn lộ trình học tập cá nhân", included: false }
                    ]
                },
                {
                    id: 'pro',
                    name: 'PRO',
                    desc: 'Tìm việc nhanh chóng',
                    price: '49,000đ',
                    unit: '/tháng',
                    badge: 'Best Seller',
                    isBestSeller: true,
                    features: [
                        { text: "Huy hiệu PRO nổi bật hồ sơ", included: true },
                        { text: "Đánh giá & Tối ưu CV chuyên sâu", included: true },
                        { text: "Ứng tuyển & Làm test Không giới hạn", included: true },
                        { text: "Phỏng vấn thử mô phỏng (30p/ngày)", included: true },
                        { text: "Xem độ phù hợp của CV với công việc", included: true },
                        { text: "Xem biểu đồ phân tích năng lực", included: false },
                        { text: "Cố vấn lộ trình học tập cá nhân", included: false }
                    ]
                },
                {
                    id: 'premium',
                    name: 'PREMIUM',
                    desc: 'Bứt phá thu nhập',
                    price: '99,000đ',
                    unit: '/tháng',
                    isPremium: true,
                    features: [
                        { text: "Tất cả quyền lợi gói PRO", included: true, highlight: true },
                        { text: "Gợi ý khóa học theo năng lực thực tế", included: true, highlight: true },
                        { text: "Cố vấn sự nghiệp & Lộ trình thăng tiến", included: true, highlight: true},
                        { text: "Phỏng vấn thử mô phỏng (60p/ngày)", included: true, highlight: true },
                        { text: "Xem biểu đồ phân tích năng lực", included: true, highlight: true}
                    ]
                }
            ]
        },
        business: {
    title: "Tuyển dụng nhân tài đỉnh cao",
    subtitle: "Giải pháp tuyển dụng thông minh giúp tiết kiệm 80% thời gian sàng lọc.",
    packs: [
        {
            id: 'business_free',
            name: 'FREE', // Đổi tên cho ngắn gọn
            desc: 'Dành cho Start-up nhỏ',
            price: '0đ',
            unit: '/vĩnh viễn',
            features: [
                { text: "Đăng 1 tin tuyển dụng (30 ngày)", included: true }, // Tăng lên 30 ngày
                { text: "Tạo 1 bài Test năng lực cơ bản", included: true },
                { text: "Xem 5 hồ sơ ứng viên/tháng", included: true },
                { text: "AI tạo JD tự động", included: true }, // Thêm chữ AI cho sang
                { text: "Hệ thống chống gian lận (AI Monitor)", included: false },
                { text: "Phân tích chuyên sâu & Xếp hạng", included: false } 
            ]
        },
        {
            id: 'business_pro', 
            name: 'PRO',
            desc: 'Tăng tốc tuyển dụng',
            price: '1,500,000đ',
            unit: '/tháng',
            badge: 'Khuyên dùng', // Đổi badge
            isBestSeller: true,
            features: [
                { text: "Đăng 10 tin tuyển dụng/tháng", included: true },
                { text: "Tạo 10 bài Test năng lực riêng", included: true }, // Tăng số lượng test
                { text: "Xem 100 hồ sơ ứng viên/tháng", included: true },
                { text: "Chống gian lận cơ bản (Cảnh báo tab)", included: true }, // Thêm tính năng này
                { text: "Tự động tạo Test bằng AI", included: true }, 
                { text: "Quảng cáo Banner trang chủ", included: false }
            ]
        },
        {
            id: 'business_premium', 
            name: 'PREMIUM', // Tên nghe "vip" hơn
            desc: 'Giải pháp toàn diện (6 Tháng)',
            price: '8,000,000đ',
            unit: '/6 tháng',
            isPremium: true, // Để style màu khác
            features: [
                { text: "Tin đăng & Xem hồ sơ KHÔNG GIỚI HẠN", included: true, highlight: true }, // Gộp dòng cho gọn
                { text: "Huy hiệu Top & Đẩy tin Hot Job", included: true, highlight: true },
                { text: "Chống gian lận nâng cao (Webcam/AI)", included: true, highlight: true }, 
                { text: "Phân tích & Xếp hạng Ứng viên (AI)", included: true, highlight: true }, 
                { text: "CSKH Ưu tiên 24/7", included: true }, // Thêm dịch vụ support
                { text: "Quảng cáo Banner trang chủ", included: true }
            ]
        }
    ]
}
    };

    const currentConfig = PLANS[currentConfigKey];

    const RenderButton = ({ planKey, isPremium }) => {
        const currentRank = PLAN_LEVELS[userPlan] || 0;
        const targetRank = PLAN_LEVELS[planKey] || 0;
        const isCurrent = userPlan === planKey;

        if (isCurrent) return (
            <div className="mb-8">
                <button disabled className="w-full py-3 bg-green-100 text-green-700 font-bold rounded-xl cursor-default border border-green-200 flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-lg">check_circle</span> Đang sử dụng
                </button>
                {expiryDate && <p className="text-xs text-green-600 text-center mt-2 font-medium">Hết hạn: {new Date(expiryDate).toLocaleDateString('vi-VN')} <br/> (Còn {getDaysRemaining()} ngày)</p>}
            </div>
        );

        if (planKey.includes('free')) return <button disabled className="w-full py-3 font-bold rounded-xl mb-8 border border-gray-200 text-gray-300 bg-gray-50 cursor-not-allowed">Gói miễn phí</button>;

        return <button onClick={() => handleUpgrade(planKey)} disabled={loading} className={`w-full py-3 font-bold rounded-xl mb-8 shadow-lg transition-all hover:scale-105 disabled:opacity-70 ${isPremium ? 'bg-yellow-500 hover:bg-yellow-400 text-slate-900 shadow-yellow-500/20' : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-200'}`}>{loading ? 'Đang xử lý...' : (targetRank > currentRank ? 'Nâng Cấp Ngay' : 'Đăng Ký Ngay')}</button>;
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] pt-28 pb-20 px-4 font-sans relative">
            
            {/* ✅ CONTAINER CHỨA TOAST - Đặt ở cuối để luôn đè lên trên */}
            <ToastContainer 
                position="bottom-left"
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />

            <div className="max-w-6xl mx-auto text-center mb-12">
                <span className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${currentConfigKey === 'business' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {currentConfigKey === 'business' ? 'Gói Doanh Nghiệp' : 'Gói Ứng Viên'}
                </span>
                <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mt-4 mb-4">{currentConfig.title}</h1>
                <p className="text-slate-500 max-w-2xl mx-auto text-lg">{currentConfig.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {currentConfig.packs.map((pack) => (
                    <div key={pack.id} className={`rounded-2xl p-8 shadow-xl relative flex flex-col transition-transform hover:-translate-y-2 ${pack.isPremium ? 'bg-gradient-to-b from-slate-900 to-slate-800 text-white border border-slate-700' : 'bg-white border border-gray-200'} ${pack.isBestSeller ? 'border-2 border-blue-500 transform md:-translate-y-4 z-10' : ''}`}>
                        {pack.isBestSeller && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-wide shadow-md">{pack.badge}</div>}
                        {pack.isPremium && <div className="absolute top-4 right-4 text-yellow-400 animate-pulse"><span className="material-symbols-outlined text-3xl">workspace_premium</span></div>}
                        <h3 className={`text-xl font-bold ${pack.isPremium ? 'text-white' : 'text-gray-900'}`}>{pack.name}</h3>
                        <p className={`${pack.isPremium ? 'text-slate-400' : 'text-gray-500'} text-sm mt-1`}>{pack.desc}</p>
                        <div className="my-6">
                            <span className={`text-4xl font-extrabold ${pack.isPremium ? 'text-white' : 'text-gray-900'}`}>{pack.price}</span>
                            <span className={`${pack.isPremium ? 'text-slate-400' : 'text-gray-500'} text-sm font-medium`}>{pack.unit}</span>
                        </div>
                        <RenderButton planKey={pack.id} isPremium={pack.isPremium} />
                        <ul className="space-y-4 text-left flex-1">
                            {pack.features.map((feature, idx) => (
                                <li key={idx} className={`flex items-center gap-3 text-sm ${feature.included ? (pack.isPremium ? 'text-slate-200' : 'text-slate-700') : 'text-gray-400 opacity-50'}`}>
                                    <span className={`material-symbols-outlined text-[20px] ${feature.included ? (pack.isPremium ? 'text-yellow-400' : 'text-green-500') : 'text-gray-400'}`}>{feature.included ? 'check_circle' : 'cancel'}</span>
                                    <span>{feature.text}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

        <div className="max-w-2xl mx-auto mt-20 text-center">
                <p className="text-gray-400 text-sm mb-6">Được tin dùng bởi các đối tác hàng đầu</p>
                <div className="flex justify-center gap-8 opacity-40 grayscale flex-wrap">
                    <span className="text-xl font-bold text-gray-600">FPT Software</span>
                    <span className="text-xl font-bold text-gray-600">VNG</span>
                    <span className="text-xl font-bold text-gray-600">Viettel</span>
                    <span className="text-xl font-bold text-gray-600">Momo</span>
                </div>
            </div>

            {/* Payment Security Note */}
            <div className="max-w-2xl mx-auto mt-16 text-center bg-white p-6 rounded-xl border border-gray-200">
                <div className="flex justify-center mb-2 text-green-600">
                    <span className="material-symbols-outlined text-3xl">lock</span>
                </div>
                <h4 className="font-bold text-gray-800">Thanh toán an toàn qua PayOS</h4>
                <p className="text-sm text-gray-500 mt-1">Hệ thống thanh toán được bảo mật bởi PayOS (Cổng thanh toán hỗ trợ VietQR, Momo, Banking).</p>
            </div>
        </div>
    );
}